import { useCallback, useEffect, useRef, useState } from "react";
import { useMqttContext } from "../contexts/MqttContext";
import { parseSimActiveInfo, type SimActiveInfo } from "../types/sim";

export interface SimModeState {
  active: boolean;
  info: SimActiveInfo | null;
}

/**
 * Simulator state hook.
 *
 * Subscribes to the retained `{prefix}sim/active` topic via the shared MQTT
 * context so every browser agrees on whether the telemetry on screen is fake.
 *
 * Must be used inside an `<MqttProvider>`.
 */
export function useSimMode(topicPrefix: string): SimModeState {
  const [info, setInfo] = useState<SimActiveInfo | null>(null);

  const prefixRef = useRef(topicPrefix);
  prefixRef.current = topicPrefix;

  const { subscribe } = useMqttContext();

  const handleMessage = useCallback((topic: string, payload: string) => {
    if (topic !== `${prefixRef.current}sim/active`) return;
    setInfo(parseSimActiveInfo(payload));
  }, []);

  useEffect(() => {
    const unsubscribe = subscribe(handleMessage);
    return unsubscribe;
  }, [subscribe, handleMessage]);

  return { active: info !== null, info };
}
